import React from 'react';
import { Progress, Skeleton, Tooltip, Typography } from 'antd';

import { InterviewChatSummary } from 'api/interviewChats/types';

import { formatRatePct } from 'helpers/interviewChats';

import styles from './FunnelStrip.module.scss';

const { Text } = Typography;

interface FunnelStripProps {
  summary: InterviewChatSummary;
  loading: boolean;
}

interface Stage {
  label: string;
  value: number;
  rate: number | null;
  color: string;
  hint: string;
}

/**
 * The adoption funnel as one thin row. Kept deliberately short: the conversations
 * below are what this page is for, and three stacked metric blocks pushed them
 * under the fold.
 *
 * It describes every interview chat, not the filtered list underneath — the numbers come
 * from one unfiltered call so they stay a stable denominator while you search the list.
 */
const FunnelStrip: React.FC<FunnelStripProps> = ({ summary, loading }) => {
  const neverUsed = Math.max(summary.provisioned - summary.activated, 0);

  const stages: Stage[] = [
    {
      label: 'Provisioned',
      value: summary.provisioned,
      rate: summary.provisioned ? 100 : null,
      color: '#667085',
      hint: 'Created automatically when an interview is scheduled',
    },
    {
      label: 'Used at least once',
      value: summary.activated,
      rate: summary.activationRatePct,
      color: '#2563eb',
      hint: 'At least one message from either side — the adoption number',
    },
  ];

  return (
    <div className={styles.funnelStrip}>
      {loading ? (
        <Skeleton active paragraph={{ rows: 1 }} title={{ width: '30%' }} />
      ) : (
        <>
          <div className={styles.funnelStages}>
            {stages.map((stage, index) => (
              <React.Fragment key={stage.label}>
                {index > 0 && <span className={styles.funnelArrow}>→</span>}
                <Tooltip title={stage.hint}>
                  <div className={styles.funnelStage}>
                    <div className={styles.funnelStageLabel}>{stage.label}</div>
                    <div className={styles.funnelStageValue}>
                      <span style={{ color: stage.color }}>{stage.value}</span>
                      {index > 0 && (
                        <span className={styles.funnelStageRate}>
                          {formatRatePct(stage.rate)}
                        </span>
                      )}
                    </div>
                    <Progress
                      percent={Number((stage.rate ?? 0).toFixed(1))}
                      strokeColor={stage.color}
                      trailColor="#e4e9f0"
                      showInfo={false}
                      strokeWidth={4}
                    />
                  </div>
                </Tooltip>
              </React.Fragment>
            ))}
          </div>

          <div className={styles.funnelSecondary}>
            <Text type="secondary">{neverUsed} never used</Text>
            <span className={styles.funnelDot}>·</span>
            <Text type="secondary">
              {summary.activeLast7Days} active in 7d, {summary.activeLast30Days}{' '}
              in 30d
            </Text>
          </div>
        </>
      )}
    </div>
  );
};

export default FunnelStrip;
